
import { motion } from "framer-motion";
import {
  Bed,
  Bath,
  Square,
  MapPin,
  ExternalLink,
  Heart,
  MessageSquare,
  CalendarDays,
  TrendingUp,
  Scale,
  Sparkles,
} from "lucide-react";
import {
  JL_ORIGIN,
  formatPrice,
  titleCase,
  jackLingoUrl,
  type ListingDetail,
} from "../../lib/jlApi";
import { MORTGAGE_DEFAULTS, monthlyPayment } from "../../lib/mortgage";
import { pricePerSqFt, isJustListed } from "./listingUtils";

interface ListingCardProps {
  listing: ListingDetail;
  selected: boolean;
  comingSoon?: boolean;
  isFavorite?: boolean;
  onToggleFavorite?: (listing: ListingDetail) => void;
  onInquire?: (listing: ListingDetail, mode: "ask" | "showing") => void;
  isComparing?: boolean;
  compareDisabled?: boolean;
  onToggleCompare?: (listing: ListingDetail) => void;
  onSelect: (id: number) => void;
  onHover: (id: number | null) => void;
}

export default function ListingCard({
  listing,
  selected,
  comingSoon,
  isFavorite,
  onToggleFavorite,
  onInquire,
  isComparing,
  compareDisabled,
  onToggleCompare,
  onSelect,
  onHover,
}: ListingCardProps) {
  const ppsf = pricePerSqFt(listing);
  const justListed = isJustListed(listing);
  // Grounded affordability signal from the shared mortgage math (same defaults
  // as the /calculator Mortgage tab). Only shown when the price is realistic.
  const estMonthly =
    listing.price >= 50_000
      ? Math.round(monthlyPayment(listing.price).total)
      : null;

  return (
    <article
      data-listing-id={listing.id}
      onClick={() => onSelect(listing.id)}
      onMouseEnter={() => onHover(listing.id)}
      onMouseLeave={() => onHover(null)}
      className={`group cursor-pointer overflow-hidden rounded-2xl border bg-white transition active:scale-[0.99] ${
        selected
          ? "border-[var(--gold)] ring-2 ring-[var(--gold)] shadow-lg"
          : "border-[var(--sand)] hover:-translate-y-0.5 hover:border-[var(--dune)] hover:shadow-md"
      }`}
    >
      <div className="relative">
        {listing.imageUrl ? (
          <img
            src={`${JL_ORIGIN}${listing.imageUrl}`}
            alt={`${titleCase(listing.street)}, ${titleCase(listing.city)}, ${listing.state}`}
            className="h-44 w-full object-cover"
            loading="lazy"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <div className="flex h-44 w-full items-center justify-center bg-[var(--sand)]">
            <MapPin size={30} className="text-[var(--dune)]" />
          </div>
        )}

        <div className="absolute right-2 top-2 flex flex-col gap-1.5">
          {onToggleFavorite && (
            <motion.button
              whileTap={{ scale: 0.8 }}
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite(listing);
              }}
              aria-label={isFavorite ? "Remove from saved" : "Save this home"}
              aria-pressed={isFavorite}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow backdrop-blur active:scale-90"
            >
              <Heart
                size={18}
                className={
                  isFavorite
                    ? "fill-[var(--gold)] text-[var(--gold)]"
                    : "text-[var(--navy)]"
                }
              />
            </motion.button>
          )}
          {onToggleCompare && (
            <motion.button
              whileTap={{ scale: 0.8 }}
              disabled={compareDisabled && !isComparing}
              onClick={(e) => {
                e.stopPropagation();
                onToggleCompare(listing);
              }}
              aria-label={isComparing ? "Remove from compare" : "Add to compare"}
              aria-pressed={isComparing}
              title={
                compareDisabled && !isComparing
                  ? "Compare up to 3 homes"
                  : isComparing
                    ? "Remove from compare"
                    : "Add to compare"
              }
              className={`flex h-9 w-9 items-center justify-center rounded-full shadow backdrop-blur active:scale-90 disabled:opacity-40 ${
                isComparing
                  ? "bg-[var(--navy)] text-white"
                  : "bg-white/90 text-[var(--navy)]"
              }`}
            >
              <Scale size={17} />
            </motion.button>
          )}
        </div>

        <div className="absolute left-2 top-2 flex flex-wrap gap-1.5">
          {justListed && (
            <span className="rounded-full bg-[var(--gold)] px-2 py-0.5 text-xs font-semibold text-white shadow">
              Just Listed
            </span>
          )}
          {comingSoon && (
            <span className="jl-new-chip relative inline-flex items-center gap-1 overflow-hidden rounded-full bg-gradient-to-r from-[var(--gold)] to-[#b8934f] px-2.5 py-0.5 text-xs font-semibold text-white shadow-[0_2px_8px_rgba(197,165,114,.5)]">
              <Sparkles size={11} className="jl-sparkle" />
              New Listing
            </span>
          )}
        </div>
      </div>

      <div className="p-4">
        <div className="flex items-baseline justify-between gap-2">
          <p
            className="text-xl font-semibold text-[var(--navy)]"
            style={{ fontFamily: "var(--font-playfair)" }}
          >
            {listing.priceDisplay || formatPrice(listing.price)}
          </p>
          {ppsf && (
            <span className="text-xs text-[var(--slate)]">
              ${ppsf.toLocaleString()}/sqft
            </span>
          )}
        </div>

        <p className="mt-1 text-sm font-medium text-[var(--navy)]">
          {titleCase(listing.street)}
        </p>
        <p className="text-sm text-[var(--slate)]">
          {titleCase(listing.city)}, {listing.state} {listing.postalCode}
        </p>

        <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-[var(--slate)]">
          {listing.bed && (
            <span className="flex items-center gap-1">
              <Bed size={14} className="text-[var(--slate-blue)]" /> {listing.bed}
            </span>
          )}
          {listing.bath && (
            <span className="flex items-center gap-1">
              <Bath size={14} className="text-[var(--slate-blue)]" /> {listing.bath}
            </span>
          )}
          {listing.sqFtTotal && (
            <span className="flex items-center gap-1">
              <Square size={14} className="text-[var(--slate-blue)]" />{" "}
              {listing.sqFtTotal} sqft
            </span>
          )}
        </div>

        {listing.subdivision && (
          <p className="mt-1 text-xs text-[var(--slate)]">
            {titleCase(listing.subdivision)}
          </p>
        )}
        <p className="mt-1 text-xs text-[var(--slate)]">
          {listing.daysListedText}
          {listing.listingId ? ` \u00b7 MLS# ${listing.listingId}` : ""}
        </p>

        {selected && (
          <div className="mt-3 flex flex-col gap-2">
            {estMonthly && (
              <div className="rounded-lg bg-[var(--cream)] p-3">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs font-medium text-[var(--slate)]">
                    Est. payment
                  </span>
                  <span className="text-sm font-semibold text-[var(--navy)]">
                    ~${estMonthly.toLocaleString()}/mo
                  </span>
                </div>
                <p className="mt-0.5 text-[11px] leading-snug text-[var(--slate)]">
                  {MORTGAGE_DEFAULTS.downPercent}% down ·{" "}
                  {MORTGAGE_DEFAULTS.rate}% · {MORTGAGE_DEFAULTS.termYears}-yr,
                  incl. est. tax + ins. Estimate only.
                </p>
                <a
                  href={`/calculator?tab=rental&price=${listing.price}`}
                  onClick={(e) => e.stopPropagation()}
                  className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-[var(--slate-blue)] hover:text-[var(--navy)]"
                >
                  <TrendingUp size={12} />
                  Estimate rental potential
                </a>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onInquire?.(listing, "ask");
                }}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[var(--navy)] px-3 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--gold)]"
              >
                <MessageSquare size={14} />
                Ask Shawn &amp; Merritt
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onInquire?.(listing, "showing");
                }}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-[var(--navy)] px-3 py-2.5 text-sm font-medium text-[var(--navy)] transition-colors hover:bg-[var(--sand)]"
              >
                <CalendarDays size={14} />
                Tour
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {onToggleCompare && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleCompare(listing);
                  }}
                  disabled={compareDisabled && !isComparing}
                  aria-pressed={isComparing}
                  className={`inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors disabled:opacity-40 ${
                    isComparing
                      ? "border-[var(--navy)] bg-[var(--navy)] text-white"
                      : "border-[var(--dune)] text-[var(--navy)] hover:bg-[var(--sand)]"
                  }`}
                >
                  <Scale
                    size={14}
                    className={isComparing ? "text-[var(--gold)]" : undefined}
                  />
                  {isComparing ? "Comparing" : "Compare"}
                </button>
              )}
              <a
                href={jackLingoUrl(listing.listingId)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className={`inline-flex items-center justify-center gap-1.5 rounded-lg border border-[var(--dune)] px-3 py-2.5 text-sm font-medium text-[var(--navy)] transition-colors hover:bg-[var(--sand)] ${
                  onToggleCompare ? "" : "col-span-2"
                }`}
              >
                <ExternalLink size={14} />
                Full details
              </a>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}
